import { useMemo } from 'react';
import { rankItem, rankings } from '@tanstack/match-sorter-utils';
import { useShallow } from 'zustand/react/shallow';
import {
  PlantsWithOverview,
  useGetPlantsQuery,
} from '@/features/plants/hooks/queries/use-get-plants.tsx';
import type {
  SetupType,
  ViewType,
} from '@/features/plants/components/shared/plants-list-view-filter-command.tsx';
import { usePlantsViewFilterStore } from '@/services/store/use-plants-view-filter-store.ts';

const EMPTY_PLANTS: PlantsWithOverview[] = [];

const getSearchableFields = (plant: PlantsWithOverview): string[] =>
  [
    plant.basicDetails.name,
    plant.technicalSpecifications.setupType?.replaceAll('-', ' '),
  ].filter((value): value is string => Boolean(value));

const rankPlant = (plant: PlantsWithOverview, needle: string): number => {
  let best: number = rankings.NO_MATCH;
  for (const field of getSearchableFields(plant)) {
    const { rank } = rankItem(field, needle);
    if (rank > best) best = rank;
  }
  return best;
};

const matchesSetup = (plant: PlantsWithOverview, setupType: SetupType, view: ViewType) => {
  const plantSetup = plant.technicalSpecifications.setupType;

  if (setupType === 'production-only') return plantSetup === 'production-only';
  if (setupType === 'storage-only') return plantSetup === 'storage-only';
  if (setupType === 'production-storage') return plantSetup === 'production-storage';

  if (view === 'production') {
    return plantSetup === 'production-storage' || plantSetup === 'production-only';
  }
  if (view === 'storage') {
    return plantSetup === 'production-storage' || plantSetup === 'storage-only';
  }
  return true;
};

export const usePlantsFilter = (): {
  plants: PlantsWithOverview[];
  isLoading: boolean;
} => {
  const { data, isLoading } = useGetPlantsQuery({ includeOverview: true });

  const { search, setupType, view, showArchived } = usePlantsViewFilterStore(
    useShallow((state) => ({
      search: state.search,
      setupType: state.setupType,
      view: state.view,
      showArchived: state.showArchived,
    }))
  );

  const plants = useMemo(() => {
    const source = data ?? EMPTY_PLANTS;
    const base = source.filter((plant) => {
      if (!showArchived && plant.basicDetails.status === 'Archived') return false;
      return matchesSetup(plant, setupType, view);
    });

    const needle = search.trim();
    if (!needle) return base;

    return base
      .map((plant) => ({ plant, rank: rankPlant(plant, needle) }))
      .filter(({ rank }) => rank > rankings.NO_MATCH)
      .sort((a, b) => b.rank - a.rank)
      .map(({ plant }) => plant);
  }, [data, showArchived, setupType, view, search]);

  return { plants, isLoading };
};
